import {
  ExclamationTriangleIcon,
  FireIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { joinClassNames } from "@utils/helpers";
import { TimesType } from "@features/eventPage/types";
import ShowingCountDown from "@features/eventPage/components/showingCountDown";
import { EventShowing, EventShowingsMsgs } from "@types";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import ShowingStatusToolTip from "@features/eventListMode/components/showingStatusToolTip";
import { KeyedMutator } from "swr";
import { motion } from "framer-motion";
import { Badge, Card, Text } from "@components/theming";
import { useIsShopsite } from "@context/DomainContext";
import { useTranslations } from "next-intl";

interface Props {
  times: TimesType[];
  selectedTime?: EventShowing;
  mutate: KeyedMutator<EventShowingsMsgs>;
  handleSelectTime: (id: number | null) => void;
  bookedTimeTickets: (id: number) => number | undefined;
}

export default function EventTimePicker({
  times,
  selectedTime,
  handleSelectTime,
  bookedTimeTickets,
  mutate,
}: Props) {
  const isShopsite = useIsShopsite();
  const t = useTranslations("EventPage");

  return (
    <div
      data-testid="showingtime"
      role="list"
      className={
        selectedTime
          ? "relative z-10 w-full sm:w-fit h-full align-top mb-4"
          : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      }
    >
      {times.map((time, index) => (
        <Card
          asChild
          key={time.id}
          sharedClasses={joinClassNames(
            selectedTime && selectedTime.id !== time.id ? "max-sm:hidden" : "",
            times.length > 1 && time.availability?.selectable
              ? "cursor-pointer"
              : !time.availability?.selectable
              ? "cursor-not-allowed"
              : "cursor-default",
            "w-full"
          )}
          whitelabelClasses={
            selectedTime?.id === time.id
              ? "bg-[color:var(--accent-9)] before:bg-[color:var(--gray-2)]"
              : !time.availability?.selectable
              ? "bg-[color:var(--gray-a3)] text-[color:var(--gray-a11)]"
              : ""
          }
          shopsiteClasses={joinClassNames(
            selectedTime?.id === time.id
              ? "bg-indigo-50 ring-2 ring-indigo-500"
              : !time.availability?.selectable
              ? "bg-gray-100 text-gray-700"
              : "bg-white",
            "px-4 py-2 rounded-md border border-gray-100 shadow transition-colors ease-in-out duration-200 h-full"
          )}
        >
          <motion.button
            initial={{ opacity: 0, x: "1rem" }}
            animate={
              selectedTime && selectedTime.id === time.id
                ? { opacity: [0, 1], x: ["1rem", "0rem"] }
                : {
                    opacity: 1,
                    x: 0,
                    transition: { delay: index * 0.1 + 0.2 },
                  }
            }
            whileTap={
              times.length > 1 && time.availability?.selectable
                ? {
                    scale: 0.95,
                  }
                : undefined
            }
            key={time.id}
            className={joinClassNames(
              selectedTime && selectedTime.id !== time.id ? "hidden" : "",
              "col-span-1 relative w-full",
              time.availability?.selectable && times.length > 1
                ? "cursor-pointer"
                : ""
            )}
            onClick={() => {
              if (!time.availability?.selectable) return;
              handleSelectTime(
                selectedTime?.id === time.id && times.length > 1
                  ? null
                  : time.id
              );
            }}
          >
            <div className="flex flex-col justify-between gap-1 w-full">
              <div className="flex flex-1 items-center justify-between gap-2 truncate">
                <div className="flex items-center gap-1 truncate">
                  {time?.availability?.icon === "shield" ? (
                    <ShieldExclamationIcon
                      className={joinClassNames(
                        "h-5 w-5 shrink-0",
                        isShopsite ? "text-gray-500" : ""
                      )}
                    />
                  ) : time?.availability?.icon === "warning" ? (
                    <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 shrink-0" />
                  ) : time?.availability?.icon === "fire" ? (
                    <FireIcon className="h-6 w-6 text-orange-400 shrink-0" />
                  ) : time?.availability?.icon === "closed" ? (
                    <XCircleIcon className="h-5 w-5 text-red-600 shrink-0" />
                  ) : (
                    <></>
                  )}
                  <Text sharedClasses="font-medium truncate">
                    {time.displayDate ? time.displayDate : time.name}
                  </Text>
                </div>
                {!time.displayName &&
                selectedTime?.id === time.id &&
                times.length > 1 ? (
                  <div className="flex gap-1 items-center shrink-0">
                    <ArrowPathIcon
                      className={joinClassNames(
                        "h-4 w-4",
                        isShopsite
                          ? "text-gray-500"
                          : "text-[color:var(--gray-a11)]"
                      )}
                    />
                    <Text
                      color="gray"
                      sharedClasses="text-sm font-medium"
                      shopsiteClasses="text-gray-500"
                    >
                      {t("calendarmode.changetime")}
                    </Text>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              {time.displayName ? (
                <div className="flex items-center justify-between gap-2 truncate">
                  <Text
                    color="gray"
                    shopsiteClasses="text-gray-500"
                    sharedClasses="text-left truncate"
                  >
                    {time.displayName}
                  </Text>
                  {selectedTime?.id === time.id && times.length > 1 ? (
                    <div className="flex gap-1 items-center shrink-0">
                      <ArrowPathIcon
                        className={joinClassNames(
                          "h-4 w-4",
                          isShopsite
                            ? "text-gray-500"
                            : "text-[color:var(--gray-a11)]"
                        )}
                      />
                      <Text
                        color="gray"
                        sharedClasses="text-sm font-medium"
                        shopsiteClasses="text-gray-500"
                      >
                        {t("calendarmode.changetime")}
                      </Text>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ) : (
                <></>
              )}
            </div>
            {!time.availability?.selectable && (
              <div
                className={joinClassNames(
                  "z-10 absolute -right-2 -top-4 flex items-center gap-1 h-6 w-auto px-2 py-1 rounded-full font-medium text-sm",
                  time.availability?.bgColor ?? ""
                )}
              >
                {time.seconds && time.seconds > 0 ? (
                  <>
                    <ShowingCountDown
                      ttl={time.seconds}
                      title={time.availability?.title ?? ""}
                      textColor={time.availability?.textColor}
                      mutate={mutate}
                    />
                  </>
                ) : (
                  <span className={time.availability?.textColor}>
                    {time.availability?.title}
                  </span>
                )}
                <ShowingStatusToolTip
                  title={time.availability?.title ?? ""}
                  description={time.availability?.description ?? ""}
                  statusId={time.statusId}
                  accessType={time.accessType}
                />
              </div>
            )}
            {bookedTimeTickets(time.id) ? (
              <Badge
                sharedClasses="absolute z-10 -right-2 -top-4 !text-xs"
                shopsiteClasses="!rounded-lg"
                variant="solid"
              >
                {t("amountincart", { amount: bookedTimeTickets(time.id) })}
              </Badge>
            ) : (
              <></>
            )}
          </motion.button>
        </Card>
      ))}
    </div>
  );
}
